import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Activity, Moon, Sun, Cloud } from "lucide-react";

/**
 * Right Now — a single GREEN / AMBER / RED verdict for the next few hours.
 *
 * Synthesises Lunar phase + Circadian band + Weather Power into one call.
 * Sits between the existing strips on the Brief (per No. 6, iter 30.6,
 * 19 Feb 2026). Aggregation rule: any GREEN+RED mix forces AMBER —
 * never extreme unless every signal agrees.
 *
 * Backed by GET /api/sight/right-now. tz_offset_hours is computed from
 * the browser so the circadian band reflects the operator's local time.
 */
export default function RightNowPanel() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        const load = async () => {
            try {
                // -minutes/60 because JS's getTimezoneOffset returns inverse sign
                const tz = -new Date().getTimezoneOffset() / 60;
                const { data: d } = await api.get(`/sight/right-now?tz_offset_hours=${tz}`);
                setData(d);
            } catch (e) {
                setErr(e?.response?.data?.detail || e.message || "Right-Now signal lost");
            } finally {
                setBusy(false);
            }
        };
        load();
    }, []);

    if (busy) {
        return (
            <div
                className="paper-card border border-ink/15 p-4 flex items-center gap-2"
                data-testid="right-now-loading"
            >
                <Loader2 className="w-3 h-3 animate-spin text-ink-soft" />
                <span className="font-body text-xs text-ink-soft uppercase tracking-[0.2em]">
                    Reading the room…
                </span>
            </div>
        );
    }
    if (err || !data) {
        return null; // Silent fail — don't clutter the Brief if a signal hiccups.
    }

    const styleByVerdict = {
        GREEN: {
            border: "border-stamp-green",
            text: "text-stamp-green",
            bg: "bg-stamp-green/5",
            dot: "bg-stamp-green",
        },
        AMBER: {
            border: "border-stamp-amber",
            text: "text-stamp-amber",
            bg: "bg-stamp-amber/5",
            dot: "bg-stamp-amber",
        },
        RED: {
            border: "border-stamp-red",
            text: "text-stamp-red",
            bg: "bg-stamp-red/5",
            dot: "bg-stamp-red",
        },
    };
    const s = styleByVerdict[data.verdict] || styleByVerdict.AMBER;

    return (
        <div
            className={`paper-card border-2 ${s.border} ${s.bg} p-5`}
            data-testid="right-now-panel"
            data-verdict={data.verdict}
        >
            <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-1 shrink-0">
                    <div
                        className={`w-12 h-12 rounded-full ${s.dot} sf-glow flex items-center justify-center`}
                        aria-hidden
                    >
                        <Activity className="w-6 h-6 text-paper-light" strokeWidth={2.4} />
                    </div>
                    <div className={`font-heading text-[10px] tracking-[0.22em] ${s.text} uppercase`}>
                        {data.verdict}
                    </div>
                </div>
                <div className="flex-1 leading-snug">
                    <div className="label-typed text-[9px]">Right now · next few hours</div>
                    <div
                        className={`font-heading text-base md:text-lg ${s.text} uppercase tracking-[0.06em] mt-0.5`}
                        data-testid="right-now-narrative"
                    >
                        {data.narrative}
                    </div>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-body text-ink leading-snug">
                <SignalRow
                    icon={<Moon className="w-3 h-3" />}
                    label={`Lunar · ${data.lunar.phase}`}
                    verdict={data.lunar.verdict}
                    phrase={data.lunar.phrase}
                    s={styleByVerdict[data.lunar.verdict] || styleByVerdict.AMBER}
                    testid="right-now-lunar"
                />
                <SignalRow
                    icon={<Sun className="w-3 h-3" />}
                    label={`Circadian · ${data.circadian.band}`}
                    verdict={data.circadian.verdict}
                    phrase={data.circadian.phrase}
                    s={styleByVerdict[data.circadian.verdict] || styleByVerdict.AMBER}
                    testid="right-now-circadian"
                />
                <SignalRow
                    icon={<Cloud className="w-3 h-3" />}
                    label={
                        data.weather.available
                            ? `Weather · ${data.weather.band}`
                            : "Weather · n/a"
                    }
                    verdict={data.weather.verdict || "—"}
                    phrase={data.weather.phrase}
                    s={data.weather.verdict
                        ? (styleByVerdict[data.weather.verdict] || styleByVerdict.AMBER)
                        : { border: "border-ink/15", text: "text-ink-soft", bg: "", dot: "bg-ink/30" }
                    }
                    testid="right-now-weather"
                />
            </div>
        </div>
    );
}

function SignalRow({ icon, label, verdict, phrase, s, testid }) {
    return (
        <div
            className={`border-l-2 ${s.border} pl-2`}
            data-testid={testid}
            data-verdict={verdict}
        >
            <div className={`flex items-center gap-1 font-heading text-[10px] uppercase tracking-[0.18em] ${s.text}`}>
                {icon}
                <span>{label}</span>
                <span className="ml-auto opacity-80">{verdict}</span>
            </div>
            <div className="text-ink-soft mt-0.5">{phrase}</div>
        </div>
    );
}
